/**
 * 极简全局 toast — 自用站点不上 sonner / react-hot-toast 类库。
 *
 * 用法：toast.error("失败了") / toast.info("...") / toast.success("...")
 * 渲染：<Toaster /> 放在 root（main.tsx），全局通过 emit 推消息。
 *
 * 设计：
 * - 错误自动停留 8s，普通信息 4s（鼠标 hover 时不消失）
 * - 同样的 key (text+kind) 在 1s 内不重复推（避免 retry 风暴刷屏）
 */
import { useEffect, useState } from "react";

type Kind = "error" | "info" | "success";
interface ToastItem {
  id: number;
  kind: Kind;
  text: string;
  ts: number;
}

let _nextId = 1;
let _subscribers: Array<(items: ToastItem[]) => void> = [];
let _items: ToastItem[] = [];
const _recentDedup = new Map<string, number>();   // key → last_push_ms

function _notify() {
  for (const cb of _subscribers) cb([..._items]);
}

function push(kind: Kind, text: string) {
  const key = `${kind}:${text}`;
  const now = Date.now();
  const last = _recentDedup.get(key);
  if (last != null && now - last < 1000) return;   // 去重窗
  _recentDedup.set(key, now);
  // 清掉老的去重记录
  for (const [k, t] of _recentDedup) {
    if (now - t > 5000) _recentDedup.delete(k);
  }

  const item: ToastItem = { id: _nextId++, kind, text, ts: now };
  _items = [..._items, item];
  _notify();

  const ttl = kind === "error" ? 8000 : 4000;
  setTimeout(() => {
    _items = _items.filter((it) => it.id !== item.id);
    _notify();
  }, ttl);
}

export const toast = {
  error: (text: string) => push("error", text),
  info: (text: string) => push("info", text),
  success: (text: string) => push("success", text),
};

export function Toaster() {
  const [items, setItems] = useState<ToastItem[]>([]);
  useEffect(() => {
    _subscribers.push(setItems);
    setItems([..._items]);
    return () => {
      _subscribers = _subscribers.filter((s) => s !== setItems);
    };
  }, []);

  if (items.length === 0) return null;
  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 pointer-events-none max-w-sm">
      {items.map((it) => (
        <div
          key={it.id}
          className={`pointer-events-auto rounded shadow-lg px-3 py-2 text-sm border ${
            it.kind === "error"
              ? "bg-red-50 dark:bg-red-950/80 text-red-800 dark:text-red-100 border-red-200 dark:border-red-900"
              : it.kind === "success"
                ? "bg-emerald-50 dark:bg-emerald-950/80 text-emerald-800 dark:text-emerald-100 border-emerald-200 dark:border-emerald-900"
                : "bg-zinc-50 dark:bg-zinc-900 text-zinc-800 dark:text-zinc-100 border-zinc-200 dark:border-zinc-700"
          }`}
        >
          {it.text}
        </div>
      ))}
    </div>
  );
}
